<template>
  <component
    :is="embedded ? 'div' : 'v-container'"
    :fluid="!embedded || undefined"
    :class="
      embedded
        ? 'intelligence-org-hierarchy'
        : 'intelligence-org-hierarchy pa-4'
    "
  >
    <div v-if="!embedded" class="mb-6">
      <h1 class="text-h5 font-weight-bold mb-1 theme-heading">
        Organizational Hierarchy
      </h1>
      <p class="text-subtitle-2 grey--text text--darken-1 mb-0">
        AI-inferred organizational structure from
        {{
          orgTotalProfiles > 0 ? orgTotalProfiles.toLocaleString() : "..."
        }}
        profiles
      </p>
    </div>

    <v-progress-linear
      v-if="isLoadingOrg"
      indeterminate
      color="#007AFF"
      class="mb-4"
    />

    <div v-if="availableDepartments.length > 0" class="mb-6">
      <div class="text-overline grey--text text--darken-1 mb-2">
        Department Filter
      </div>
      <div class="d-flex flex-wrap" style="gap: 8px">
        <v-chip
          :outlined="selectedDepartment !== null"
          :color="selectedDepartment === null ? '#007AFF' : undefined"
          :text-color="selectedDepartment === null ? 'white' : undefined"
          small
          class="dept-chip"
          @click="filterByDepartment(null)"
        >
          All
        </v-chip>
        <v-chip
          v-for="dept in availableDepartments"
          :key="typeof dept === 'string' ? dept : dept.name"
          :outlined="
            selectedDepartment !== (typeof dept === 'string' ? dept : dept.name)
          "
          :color="
            selectedDepartment === (typeof dept === 'string' ? dept : dept.name)
              ? '#007AFF'
              : undefined
          "
          :text-color="
            selectedDepartment === (typeof dept === 'string' ? dept : dept.name)
              ? 'white'
              : undefined
          "
          small
          class="dept-chip"
          @click="
            filterByDepartment(typeof dept === 'string' ? dept : dept.name)
          "
        >
          {{ typeof dept === "string" ? dept : dept.name }}
          {{
            typeof dept === "object" && dept.count ? "(" + dept.count + ")" : ""
          }}
        </v-chip>
      </div>
    </div>

    <div v-if="levelCountsEntries.length > 0" class="level-counts-bar mb-8">
      <div
        v-for="entry in levelCountsEntries"
        :key="entry.level"
        class="seniority-bar-row"
      >
        <div class="seniority-bar-label">
          <span
            class="level-dot"
            :style="{ background: getSeniorityColor(entry.level) }"
          ></span>
          <span class="text-caption grey--text text--darken-1">{{
            entry.level
          }}</span>
        </div>
        <div class="seniority-bar-track">
          <div
            class="seniority-bar-fill"
            :style="{
              width:
                maxLevelCount > 0
                  ? Math.max((entry.count / maxLevelCount) * 100, 4) + '%'
                  : '4%',
              background: getSeniorityColor(entry.level),
            }"
          ></div>
        </div>
        <span
          class="level-count-badge"
          :style="{
            color: getSeniorityColor(entry.level),
            background: getSeniorityColor(entry.level) + '20',
          }"
        >
          {{ entry.count }}
        </span>
      </div>
    </div>

    <div v-if="orgError" class="error-banner mb-6">
      {{ orgError }}
    </div>

    <div
      v-if="!isLoadingOrg && hierarchyData.length === 0"
      class="text-center py-12 grey--text"
    >
      <div style="font-size: 48px" class="mb-4">🏢</div>
      <div class="text-subtitle-1 font-weight-medium mb-2">
        No profiles found
      </div>
      <div class="text-body-2">Try selecting a different department filter</div>
    </div>

    <div
      v-if="!isLoadingOrg && hierarchyData.length > 0"
      class="timeline-container"
    >
      <div class="timeline-line"></div>

      <div
        v-for="level in hierarchyData"
        :key="level.level"
        class="timeline-level mb-8"
      >
        <div
          class="timeline-node"
          :style="{
            background: getSeniorityColor(level.level),
            border: '3px solid #FAFAFA',
          }"
        ></div>

        <div
          class="text-h6 font-weight-bold mb-4 pl-2"
          :style="{ color: getSeniorityColor(level.level) }"
        >
          {{ level.level }} ({{ level.count }})
        </div>

        <div class="d-flex flex-wrap pl-2" style="gap: 12px">
          <v-card
            v-for="member in visibleMembers(level)"
            :key="member.profile_id || member.profileId"
            outlined
            class="member-card"
            @click="viewProfile(member.profile_id || member.profileId)"
          >
            <v-card-text class="pa-4">
              <div class="text-subtitle-2 font-weight-bold mb-1 theme-heading">
                {{ member.name }}
              </div>
              <div
                class="text-body-2 font-weight-medium mb-1"
                style="color: #007AFF"
              >
                {{ member.title || "Professional" }}
              </div>
              <div v-if="member.company" class="text-caption grey--text mb-2">
                {{ member.company }}
              </div>
              <div class="d-flex align-center mb-2" style="gap: 6px">
                <v-chip
                  x-small
                  :color="getSeniorityColor(level.level) + '20'"
                  :text-color="getSeniorityColor(level.level)"
                  label
                >
                  {{ member.department }}
                </v-chip>
                <span
                  v-if="
                    member.achievements_count > 0 ||
                      member.achievementsCount > 0
                  "
                  class="text-caption grey--text"
                >
                  {{
                    member.achievements_count || member.achievementsCount
                  }}
                  achievement{{
                    (member.achievements_count || member.achievementsCount) !==
                    1
                      ? "s"
                      : ""
                  }}
                </span>
              </div>
              <div
                v-if="memberSkills(member).length > 0"
                class="d-flex flex-wrap"
                style="gap: 4px"
              >
                <v-chip
                  v-for="(skill, idx) in memberSkills(member).slice(0, 4)"
                  :key="idx"
                  x-small
                  color="#E3F2FD"
                  text-color="#007AFF"
                  label
                >
                  {{ typeof skill === "string" ? skill : skill.name || "" }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>
          <div
            v-if="hasMoreMembers(level)"
            class="more-members-indicator d-flex align-center"
          >
            <v-chip
              small
              outlined
              :color="getSeniorityColor(level.level)"
              class="more-chip"
              @click="toggleExpand(level.level)"
            >
              <v-icon small left>{{
                isExpanded(level.level) ? "mdi-chevron-up" : "mdi-chevron-down"
              }}</v-icon>
              {{
                isExpanded(level.level)
                  ? "Show less"
                  : "and " + hiddenMemberCount(level) + " more"
              }}
            </v-chip>
          </div>
        </div>
      </div>
    </div>
  </component>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

const SENIORITY_COLORS = {
  "C-Suite": "#8b5cf6",
  VP: "#6366f1",
  Director: "#3b82f6",
  Manager: "#14b8a6",
  Senior: "#22c55e",
  "Mid-Level": "#9ca3af",
  Junior: "#f59e0b",
};

const HIERARCHY_ORDER = [
  "C-Suite",
  "VP",
  "Director",
  "Manager",
  "Senior",
  "Mid-Level",
  "Junior",
];

export default {
  name: "IntelligenceOrgHierarchy",
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      orgError: null,
      expandedLevels: {},
      defaultVisible: 12,
    };
  },
  computed: {
    ...mapState("intelligenceTalent", [
      "isLoadingOrg",
      "selectedDepartment",
      "orgHierarchy",
    ]),
    ...mapGetters("intelligenceTalent", [
      "orgHierarchyData",
      "orgLevelCounts",
      "orgTotalProfiles",
      "orgDepartments",
    ]),
    hierarchyData() {
      return this.orgHierarchyData || [];
    },
    availableDepartments() {
      return this.orgDepartments || [];
    },
    levelCountsEntries() {
      const counts = this.orgLevelCounts;
      if (!counts) return [];
      return HIERARCHY_ORDER.filter(
        (level) => counts[level] !== undefined,
      ).map((level) => ({ level, count: counts[level] || 0 }));
    },
    maxLevelCount() {
      if (!this.levelCountsEntries.length) return 0;
      return Math.max(...this.levelCountsEntries.map((e) => e.count));
    },
  },
  mounted() {
    this.loadOrgHierarchy(null);
  },
  methods: {
    ...mapActions("intelligenceTalent", ["loadOrgHierarchy"]),
    getSeniorityColor(level) {
      return SENIORITY_COLORS[level] || "#9ca3af";
    },
    filterByDepartment(dept) {
      this.orgError = null;
      this.expandedLevels = {};
      this.loadOrgHierarchy(dept);
    },
    visibleMembers(level) {
      var members = level.members || [];
      if (this.expandedLevels[level.level]) return members;
      return members.slice(0, this.defaultVisible);
    },
    hasMoreMembers(level) {
      return (level.members || []).length > this.defaultVisible;
    },
    hiddenMemberCount(level) {
      return Math.max(0, (level.members || []).length - this.defaultVisible);
    },
    isExpanded(levelName) {
      return !!this.expandedLevels[levelName];
    },
    toggleExpand(levelName) {
      this.$set(
        this.expandedLevels,
        levelName,
        !this.expandedLevels[levelName],
      );
    },
    memberSkills(member) {
      return member.skills || [];
    },
    viewProfile(profileId) {
      if (!profileId) return;
      this.$router.push({
        name: "IntelligenceProfile",
        params: { profileId },
        query: { from: "orgchart" },
      });
    },
  },
};
</script>

<style scoped>
.intelligence-org-hierarchy {
  max-width: 1200px;
}

.dept-chip {
  cursor: pointer;
  transition: all 0.15s ease;
}

.level-counts-bar {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
}
.theme--dark .level-counts-bar {
  background: #1e1e2e;
  border-color: #444;
}

.seniority-bar-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.seniority-bar-label {
  display: flex;
  align-items: center;
  min-width: 100px;
  flex-shrink: 0;
}

.seniority-bar-track {
  flex: 1;
  height: 14px;
  background: #f0f0f0;
  border-radius: 7px;
  overflow: hidden;
}
.theme--dark .seniority-bar-track {
  background: #2a2a3a;
}

.seniority-bar-fill {
  height: 100%;
  border-radius: 7px;
  transition: width 0.4s ease;
}

.level-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
}

.level-count-badge {
  font-size: 13px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}

.error-banner {
  padding: 16px;
  background: rgba(239, 68, 68, 0.1);
  border-radius: 8px;
  color: #ef4444;
}

.timeline-container {
  position: relative;
  padding-left: 24px;
}

.timeline-line {
  position: absolute;
  left: 11px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e0e0e0;
}

.timeline-level {
  position: relative;
}

.timeline-node {
  position: absolute;
  left: -24px;
  top: 8px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  z-index: 1;
}

.member-card {
  width: 260px;
  cursor: pointer;
  transition: border-color 0.2s ease, transform 0.15s ease;
}

.member-card:hover {
  border-color: #007aff;
  transform: translateY(-2px);
}

.more-members-indicator {
  width: 100%;
  padding-top: 4px;
}

.more-chip {
  cursor: pointer;
  transition: transform 0.15s ease;
}

.more-chip:hover {
  transform: translateY(-1px);
}
</style>
